import { useSyncExternalStore } from 'react';

const DISMISS_STORAGE_KEY = 'claw-install-prompt-dismissed';
const PROMPT_SEEN_STORAGE_KEY = 'claw-install-prompt-seen';

const isBrowser = typeof window !== 'undefined';

const readStorageBoolean = (storageKey) => {
  if (!isBrowser) return false;
  try {
    return window.localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

const detectInstalled = () => {
  if (!isBrowser) return false;
  return window.matchMedia('(display-mode: standalone)').matches || Boolean(window.navigator.standalone);
};

const detectIos = () => {
  if (!isBrowser) return false;
  const userAgent = window.navigator.userAgent || '';
  const platform = window.navigator.platform || '';
  const touchPoints = window.navigator.maxTouchPoints || 0;
  return /iPad|iPhone|iPod/i.test(userAgent) || (platform === 'MacIntel' && touchPoints > 1);
};

let installPromptEvent = null;
let snapshot = {
  installPromptEvent: null,
  isInstallable: false,
  isInstalled: detectInstalled(),
  hasDismissed: readStorageBoolean(DISMISS_STORAGE_KEY),
  hasSeenPrompt: readStorageBoolean(PROMPT_SEEN_STORAGE_KEY),
  isIos: detectIos(),
};

const listeners = new Set();

const publish = (changes) => {
  snapshot = { ...snapshot, ...changes };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => snapshot;
const getServerSnapshot = () => snapshot;

const persistBoolean = (storageKey, value) => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(storageKey, String(value));
  } catch {
    // Installation still works when browser storage is unavailable.
  }
};

if (isBrowser) {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    installPromptEvent = event;
    persistBoolean(PROMPT_SEEN_STORAGE_KEY, true);
    publish({
      installPromptEvent: event,
      isInstallable: !snapshot.hasDismissed,
      hasSeenPrompt: true,
    });
  });

  window.addEventListener('appinstalled', () => {
    installPromptEvent = null;
    publish({
      installPromptEvent: null,
      isInstallable: false,
      isInstalled: true,
    });
  });

  const standaloneMedia = window.matchMedia('(display-mode: standalone)');
  const handleStandaloneChange = (event) => {
    const installed = event.matches || Boolean(window.navigator.standalone);
    if (installed) installPromptEvent = null;
    publish({
      installPromptEvent: installed ? null : installPromptEvent,
      isInstallable: installed ? false : Boolean(installPromptEvent) && !snapshot.hasDismissed,
      isInstalled: installed,
    });
  };

  if (typeof standaloneMedia.addEventListener === 'function') {
    standaloneMedia.addEventListener('change', handleStandaloneChange);
  } else if (typeof standaloneMedia.addListener === 'function') {
    standaloneMedia.addListener(handleStandaloneChange);
  }

  window.addEventListener('storage', (event) => {
    if (event.key !== DISMISS_STORAGE_KEY) return;
    const dismissed = event.newValue === 'true';
    publish({
      hasDismissed: dismissed,
      isInstallable: Boolean(installPromptEvent) && !dismissed && !snapshot.isInstalled,
    });
  });
}

const promptInstall = async () => {
  const promptEvent = installPromptEvent;
  if (!promptEvent || snapshot.isInstalled) return false;

  installPromptEvent = null;
  publish({ installPromptEvent: null, isInstallable: false });

  try {
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    const accepted = choice?.outcome === 'accepted';
    if (accepted) publish({ isInstalled: true });
    return accepted;
  } catch {
    return false;
  }
};

const dismissInstallPrompt = () => {
  persistBoolean(DISMISS_STORAGE_KEY, true);
  persistBoolean(PROMPT_SEEN_STORAGE_KEY, true);
  publish({
    hasDismissed: true,
    hasSeenPrompt: true,
    isInstallable: false,
  });
};

const useInstallPrompt = () => {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return {
    ...state,
    promptInstall,
    dismissInstallPrompt,
  };
};

export default useInstallPrompt;
